import { createActionGroup, createFeature, createReducer, on } from "@ngrx/store";

import { zefActionFailPayload, zefActionPayload, zefActionSuccessPayload } from "@vshosting/core";
import { FEATURE_NAME, PROCESSING_REPEAT_LIMIT, WAITING_REPEAT_LIMIT } from "./stripe-payment.constant";
import { CreateIntentPayload, PaymentFailureType, PaymentProcessState, PaymentProcessStatus, StripeIntentResponse, StripePaymentsState, TimeoutFailureReason } from './stripe-payment.model';
import { getPaymentClientSecretPublicPart } from './stripe-payment.util';

type RepeatedCheckResult = 'not_found' | 'processing';

/**
 * The actions supposed to be dispatched from outside.
 */
export const stripePaymentActions = createActionGroup({
  source: FEATURE_NAME,
  events: {
    /**
     * Initiates the whole payment with Stripe once the intent has been created by other means.
     * Uses paymentKey to match the intent with the 
     */
    processIntent: (paymentKey: string, clientSecret: string) =>
      zefActionPayload<{ paymentKey: string; clientSecret: string }>({ paymentKey, clientSecret }, false, false),

    /**
     * Rechecks with the API if the payment has been processed on our side.
     */
    getPaymentStatus: (paymentKey: string, clientSecretPublicPart: string) =>
      zefActionPayload<{ paymentKey: string; clientSecretPublicPart: string }>({ paymentKey, clientSecretPublicPart }, false, false),

    /**
     * Removes any traces of a previous payment with a same key.
     */
    resetPaymentByKey: (paymentKey: string) =>
      zefActionPayload<{ paymentKey: string }>({ paymentKey }),

    // The rest here is just our usual trifecta of actions to process a single API call
    createStripeIntent: (payload: CreateIntentPayload, paymentKey: string ) => 
      zefActionPayload<{ payload: CreateIntentPayload; paymentKey: string }>({ payload, paymentKey }),
    createStripeIntentSuccess: zefActionSuccessPayload<StripeIntentResponse>(),
    createStripeIntentFail: zefActionFailPayload
  }
});

/**
 * Should ONLY be used inside the library for state management.
 * For listening to success or fail, use rxjs operator functions from the neighboring .util.ts file
 */
export const internalStripePaymentActions = createActionGroup({
  source: `${FEATURE_NAME}/internal`,
  events: {
    paymentConfirmedByStripe: (paymentKey: string) =>
      zefActionPayload<{ paymentKey: string }>({ paymentKey }, false, false),

    checkPaymentStatus: (paymentKey: string, previousResult?: RepeatedCheckResult) =>
      zefActionPayload<{ paymentKey: string; previousResult?: RepeatedCheckResult }>({ paymentKey, previousResult }, false, false),

    paymentSuccess: (paymentKey: string) =>
      zefActionPayload<{ paymentKey: string }>({ paymentKey }, false, false),

    paymentTimeout: (paymentKey: string, failureReason: TimeoutFailureReason) =>
      zefActionPayload<{ paymentKey: string, failureReason: TimeoutFailureReason }>({ paymentKey, failureReason }, false, false),
    
    paymentFail: (paymentKey: string, failure: PaymentFailureType) =>
      zefActionPayload<{ paymentKey: string, failure: PaymentFailureType }>({ paymentKey, failure }, false, false),
  }
})

const initialState: StripePaymentsState = {};

export const stripePaymentsState = createFeature({
  name: FEATURE_NAME,
  reducer: createReducer(
    initialState,

    /**
     * Public actions
     */
    on(
      stripePaymentActions.getPaymentStatus,
      (s, { data: { paymentKey, clientSecretPublicPart }}): StripePaymentsState => ({
        ...s,
        [paymentKey]: {
          status: PaymentProcessStatus.CheckingPayment,
          clientSecretPublicPart,
          waitingTriesRemaining: 0,
          processingTriesRemaining: 0
        }
      })
    ),

    on(
      stripePaymentActions.processIntent,
      (s, { data: { paymentKey, clientSecret } }): StripePaymentsState => ({
        ...s,
        [paymentKey]: {
          status: PaymentProcessStatus.AwaitingConfirmation,
          clientSecretPublicPart: getPaymentClientSecretPublicPart(clientSecret)
        }
      })
    ),

    on(
      stripePaymentActions.resetPaymentByKey,
      (s, { data: { paymentKey }}): StripePaymentsState => {
        const newState = { ...s };
        delete newState[paymentKey];
        return newState;
      }
    ),

    /**
     * Internal actions
     */
    on(
      internalStripePaymentActions.paymentConfirmedByStripe,
      (s, { data: { paymentKey } }): StripePaymentsState => ({
        ...s,
        [paymentKey]: {
          status: PaymentProcessStatus.CheckingPayment,
          clientSecretPublicPart: s[paymentKey].status === PaymentProcessStatus.AwaitingConfirmation ? s[paymentKey].clientSecretPublicPart : '',
          waitingTriesRemaining: WAITING_REPEAT_LIMIT,
          processingTriesRemaining: PROCESSING_REPEAT_LIMIT,
        }
      })
    ),

    on(
      internalStripePaymentActions.checkPaymentStatus,
      (s, { data: { paymentKey, previousResult } }): StripePaymentsState => ({
        ...s,
        [paymentKey]: getStateOnCheckPayment(s[paymentKey], previousResult)
      })
    ),

    on(
      internalStripePaymentActions.paymentSuccess,
      (s, { data: { paymentKey } }): StripePaymentsState => ({
        ...s,
        [paymentKey]: { status: PaymentProcessStatus.Completed }
      })
    ),

    on(
      internalStripePaymentActions.paymentTimeout,
      (s, { data: { paymentKey, failureReason } }): StripePaymentsState => ({
        ...s,
        [paymentKey]: {
          status: PaymentProcessStatus.Unresolved,
          failure: { reason: failureReason }
        }
      })
    ),

    on(
      internalStripePaymentActions.paymentFail,
      (s, { data: { paymentKey, failure } }): StripePaymentsState => ({
        ...s,
        [paymentKey]: {
          status: PaymentProcessStatus.Failed,
          failure
        }
      })
    )
  )
});

function getStateOnCheckPayment(state: PaymentProcessState, previousResult?: RepeatedCheckResult): PaymentProcessState {
  // unless the status is already what we expect, we cannot continue
  if (state.status !== PaymentProcessStatus.CheckingPayment || !previousResult) {
    return state;
  }

  if (previousResult === 'not_found') {
    return {
      status: PaymentProcessStatus.CheckingPayment,
      clientSecretPublicPart: state.clientSecretPublicPart,
      waitingTriesRemaining: state.waitingTriesRemaining - 1,
      processingTriesRemaining: state.processingTriesRemaining
    }
  }

  else {
    return {
      status: PaymentProcessStatus.CheckingPayment,
      clientSecretPublicPart: state.clientSecretPublicPart,
      waitingTriesRemaining: 0,
      processingTriesRemaining: state.processingTriesRemaining - 1
    }
  }
}
